import React, { RefObject } from 'react';
import { useField, useForm } from 'react-final-form';

import { useIsAuthorized } from '@blocks/hooks';
import { useSessionStore } from '@blocks/stores/session';
import Link from '@blocks/ui/link';
import { ModalRef } from '@blocks/ui/modal';

import { OAuth2, onOAuth2OrderHandler } from '../_common';
import AuthModal from '../auth/auth-modal';
import { PAPER_SIZE_VALUES, USER_ROLE_CUSTOMER } from '../constants';
import providers from '../constants/auth-providers';
import { Button, Row, When } from '../ui';
import { toBoolean } from '../utils';

import Deadline from './components/deadline';
import Gdpr from './components/gdpr';
import Login from './components/login';
import Pages from './components/pages';
import Paper from './components/paper';
import PaperSize from './components/paper-size';
import Recaptcha from './components/recaptcha';
import Spy from './components/spy';
import OrderSubmitButton from './components/submit-button';
import Title from './components/title';
import Words from './components/words';
import Writer from './components/writer';
import OrderUI from './order-ui';
import { OrderProps } from './types';

type OrderFormProps = {
  domain: string;
  submitText: string;
  requestText: string;
  loginText: string;
  modalTitle: string;
  forgotPasswordLink?: string;
  registrationLink?: string;
  name: string;
  shortMode: boolean;
  showRecaptchaHint: boolean;
  debugOAuth: boolean;
  debugOAuthProviderUsed: string;
  handleSubmit: (values: any) => void;
  logoutHandler: () => void;
  submitting: boolean;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  applyMobileFlow: boolean;
  enableOAuthInitializationPreview: boolean;
  loginModal: RefObject<ModalRef>;
} & Pick<
  OrderProps,
  | 'hideLoginPrompt'
  | 'hidePaperType'
  | 'useLoginLink'
  | 'termsLabel'
  | 'termsLink'
  | 'policyLabel'
  | 'policyLink'
>;

const OrderForm: React.FC<OrderFormProps> = ({
  domain,
  submitText,
  requestText,
  loginText,
  modalTitle,
  forgotPasswordLink,
  name,
  showRecaptchaHint,
  termsLink,
  termsLabel,
  policyLink,
  policyLabel,
  debugOAuth,
  debugOAuthProviderUsed,
  handleSubmit,
  logoutHandler,
  submitting,
  loading,
  setLoading,
  shortMode,
  useLoginLink,
  hidePaperType,
  applyMobileFlow,
  enableOAuthInitializationPreview,
  registrationLink,
  hideLoginPrompt,
  loginModal,
}) => {
  const {
    session: { user_role },
    customer,
  } = useSessionStore(domain);
  const form = useForm();
  const login_ability = useField('login_ability');
  const isAuthorized = useIsAuthorized(domain);

  return (
    <OrderUI.OrderForm
      onSubmit={handleSubmit}
      data-bb-order
      name={name}
    >
      {user_role !== USER_ROLE_CUSTOMER && (
        <OAuth2
          domain={domain}
          source_context="short_order_form"
          providers={providers}
          handler={onOAuth2OrderHandler}
          isPolicyRequired
          policyModalTitle="Review our Terms to sign up"
          policyModalSubmit="Continue"
          debugOAuth={debugOAuth}
          debugOAuthProviderUsed={debugOAuthProviderUsed}
          enableOAuthInitializationPreview={enableOAuthInitializationPreview}
        />
      )}
      {applyMobileFlow && !customer.email ? null : (
        <>
          <OrderUI.OrderFields submitting={submitting}>
            <Row>
              <Login
                domain={domain}
                customer={customer}
                logoutHandler={logoutHandler}
              />
            </Row>

            {customer.email ? (
              <div>
                <OrderUI.SignedInBadge>
                  You are already signed in. Continue to your account.
                </OrderUI.SignedInBadge>
                <Button
                  type="button"
                  onClick={() => {
                    window.location.href = '/dashboard/customer/new-order';
                  }}
                >
                  Continue
                </Button>
              </div>
            ) : (
              <>
                {!toBoolean(shortMode) && (
                  <>
                    {!toBoolean(hidePaperType) && (
                      <Row>
                        <Paper domain={domain} />
                      </Row>
                    )}

                    <Row>
                      <PaperSize />
                      <When
                        field="paper-size-type"
                        is={PAPER_SIZE_VALUES.PAGES}
                      >
                        <Pages domain={domain} />
                      </When>
                      <When
                        field="paper-size-type"
                        is={PAPER_SIZE_VALUES.WORDS}
                      >
                        <Words domain={domain} />
                      </When>
                    </Row>
                    <Row>
                      <Deadline />
                    </Row>
                  </>
                )}

                <When
                  field="showGdpr"
                  is={value => Boolean(value && !customer?.email)}
                >
                  <Row>
                    <Gdpr
                      termsLink={termsLink}
                      termsLabel={termsLabel}
                      policyLink={policyLink}
                      policyLabel={policyLabel}
                    />
                  </Row>
                </When>

                <When field="showWriter">
                  <Row>
                    <Writer domain={domain} />
                  </Row>
                </When>

                <When field="showTitle">
                  <Row>
                    <Title />
                  </Row>
                </When>

                <Row>
                  <OrderSubmitButton
                    customer={customer}
                    disabled={submitting || loading}
                    requestText={requestText}
                    submitText={submitText}
                    loginText={loginText}
                  />
                </Row>
              </>
            )}
          </OrderUI.OrderFields>

          <AuthModal
            domain={domain}
            ref={loginModal}
            remote={login_ability.input.value}
            forgotPasswordLink={forgotPasswordLink}
            registrationLink={registrationLink}
            afterClose={() => setLoading(false)}
            title={modalTitle}
            onSignIn={() => {
              form.mutators.clearSubmitError();
              form.submit();
              loginModal.current?.close();
            }}
          />

          {!toBoolean(hideLoginPrompt) && !isAuthorized && (
            <OrderUI.AlreadyHaveAccount>
              Already have an account?
              {useLoginLink ? (
                <Link href={useLoginLink}>Log in</Link>
              ) : (
                <OrderUI.LoginButton
                  type="button"
                  aria-label="Log in"
                  onClick={() => {
                    if (useLoginLink) {
                      location.href = '/log-in.html';
                    }
                    loginModal.current?.open();
                  }}
                >
                  Log in
                </OrderUI.LoginButton>
              )}
            </OrderUI.AlreadyHaveAccount>
          )}
          {toBoolean(showRecaptchaHint) && !customer.email && <Recaptcha />}
          <Spy />
        </>
      )}
    </OrderUI.OrderForm>
  );
};

export default OrderForm;
